'use client';

import type { HTMLAttributes } from 'react';
import { cx } from '../utils/cx';

export type BadgeVariant =
  | 'default'
  | 'gray'
  | 'success'
  | 'warning'
  | 'error'
  | 'info'
  | 'violet'
  | 'blue'
  | 'emerald'
  | 'rose'
  | 'amber'
  | 'sky';
export type BadgeSize = 'sm' | 'md' | 'lg';

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant;
  size?: BadgeSize;
  text?: string;
}

function variantClass(variant: BadgeVariant): string {
  switch (variant) {
    case 'gray':
      return 'bg-secondary_alt text-secondary ring-secondary_alt';
    case 'success':
      return 'bg-success-secondary text-success ring-success';
    case 'warning':
      return 'bg-warning-secondary text-warning ring-warning';
    case 'error':
      return 'bg-error-secondary text-error ring-error';
    case 'info':
      return 'bg-info-secondary text-info ring-info';
    case 'violet':
      return 'bg-violet-100 text-violet-800 ring-violet-200';
    case 'blue':
      return 'bg-blue-100 text-blue-800 ring-blue-200';
    case 'emerald':
      return 'bg-emerald-100 text-emerald-800 ring-emerald-200';
    case 'rose':
      return 'bg-rose-100 text-rose-800 ring-rose-200';
    case 'amber':
      return 'bg-amber-100 text-amber-800 ring-amber-200';
    case 'sky':
      return 'bg-sky-100 text-sky-800 ring-sky-200';
    case 'default':
    default:
      return 'bg-brand-primary text-brand ring-brand';
  }
}

function sizeClass(size: BadgeSize): string {
  switch (size) {
    case 'sm':
      return 'px-2 py-0.5 text-xs';
    case 'lg':
      return 'px-3 py-1.5 text-sm';
    case 'md':
    default:
      return 'px-2.5 py-1 text-xs';
  }
}

export function Badge({
  variant = 'default',
  size = 'md',
  text,
  className,
  children,
  ...rest
}: BadgeProps) {
  return (
    <span
      {...rest}
      className={cx(
        'inline-flex items-center rounded-full font-semibold ring-1 ring-inset',
        sizeClass(size),
        variantClass(variant),
        className,
      )}
    >
      {children ?? text}
    </span>
  );
}

Badge.displayName = 'Badge';